import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, QrCode, Send } from 'lucide-react';
import { MAX_INVITES_PER_SEND, ROLES, splitEmails, type Role } from '@ready/shared';
import { useApi, type Group, type InviteCode } from '@/lib/api';
import { formatDate } from '@/lib/dates';
import { ROLE_LABELS } from '@/lib/labels';
import { Button, Card, ErrorText, Field, Input, Select, Textarea } from '@/components/ui';
import { useOrgContext } from './layout';

/**
 * Admins' most frequent task: invite people by email, or share a code, link or
 * poster; see what's pending or in use, and withdraw.
 */
export function OrgInvitesPage() {
  const { org, canManage } = useOrgContext();
  const api = useApi();
  const queryClient = useQueryClient();
  const [role, setRole] = useState<Role>('learner');
  const [maxUses, setMaxUses] = useState('');
  const [groupId, setGroupId] = useState('');
  const groups = useQuery({
    queryKey: ['groups', org.id],
    queryFn: () => api.groups(org.id),
    enabled: canManage,
  });
  const groupName = (id: string | null) =>
    groups.data?.groups.find((g) => g.id === id)?.name ?? null;
  const codes = useQuery({
    queryKey: ['inviteCodes', org.id],
    queryFn: () => api.inviteCodes(org.id),
    enabled: canManage,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['inviteCodes', org.id] });
  const create = useMutation({
    mutationFn: () =>
      api.createInviteCode(org.id, {
        role,
        maxUses: maxUses ? Number(maxUses) : undefined,
        groupId: groupId || undefined,
      }),
    onSuccess: () => {
      setMaxUses('');
      refresh();
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeInviteCode(org.id, id),
    onSuccess: refresh,
  });

  if (!canManage) return <Navigate to=".." replace />;

  return (
    <div className="space-y-6">
      <InviteByEmail groups={groups.data?.groups ?? []} />
      <PendingInvitations />

      <Card title="New invite code">
        <form
          className="grid gap-4 sm:grid-cols-[12rem_10rem_12rem_auto] sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <Field label="Joins as" htmlFor="code-role">
            <Select id="code-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {ROLES.filter((r) => r !== 'owner').map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Group (optional)" htmlFor="code-group">
            <Select id="code-group" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <option value="">No group</option>
              {groups.data?.groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Max uses" htmlFor="code-uses">
            <Input
              id="code-uses"
              type="number"
              min={1}
              placeholder="Unlimited"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={create.isPending}>
            Create code
          </Button>
        </form>
        <ErrorText error={create.error} />
      </Card>

      <Card title="Active codes">
        {codes.data?.codes.length === 0 ? (
          <p className="text-sm text-muted-foreground">No active codes yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {codes.data?.codes.map((c) => (
              <CodeRow
                key={c.id}
                code={c}
                groupName={groupName(c.groupId)}
                onRevoke={() => revoke.mutate(c.id)}
                revoking={revoke.isPending}
              />
            ))}
          </ul>
        )}
        <ErrorText error={revoke.error} />
      </Card>

      <p className="text-sm text-muted-foreground">
        Each code has a <span className="font-medium text-foreground">Poster</span> to print or
        share, with a QR code to join and a message to send with it. Prefer automatic joining?{' '}
        <Link to="../settings" className="text-primary">
          Verify your email domain
        </Link>{' '}
        and people with that email join when they sign in.
      </p>
    </div>
  );
}

function CodeRow({
  code,
  groupName,
  onRevoke,
  revoking,
}: {
  code: InviteCode;
  groupName: string | null;
  onRevoke: () => void;
  revoking: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/join/${code.code}`;
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
      <div className="space-y-0.5">
        <p className="font-mono text-base font-semibold tracking-widest">{code.code}</p>
        <p className="text-muted-foreground">
          Joins as {ROLE_LABELS[code.role].toLowerCase()}
          {groupName ? ` in ${groupName}` : ''} · used {code.uses}
          {code.maxUses ? ` of ${code.maxUses}` : ''} times
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(link);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            } catch {
              // Clipboard blocked; the code is visible to copy by hand.
            }
          }}
        >
          {copied ? (
            <Check className="size-4" aria-hidden />
          ) : (
            <Copy className="size-4" aria-hidden />
          )}
          {copied ? 'Copied' : 'Copy link'}
        </Button>
        <Link
          to={`${code.id}/poster`}
          className="inline-flex items-center justify-center gap-2 rounded-[var(--radius)] border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-muted"
        >
          <QrCode className="size-4" aria-hidden />
          Poster
        </Link>
        <Button variant="ghost" disabled={revoking} onClick={onRevoke}>
          Revoke
        </Button>
      </div>
    </li>
  );
}

const INVITE_ROLES = ROLES.filter((r) => r !== 'owner');

/** Invite people by email, each with a one-click link that works only for their address. */
function InviteByEmail({ groups }: { groups: Group[] }) {
  const { org } = useOrgContext();
  const api = useApi();
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [role, setRole] = useState<Role>('learner');
  const [groupId, setGroupId] = useState('');
  const [message, setMessage] = useState('');
  const emails = splitEmails(text);
  const bad = emails.filter((e) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
  const send = useMutation({
    mutationFn: () =>
      api.invite(org.id, {
        emails,
        role: role as Exclude<Role, 'owner'>,
        groupId: groupId || null,
        message: message.trim() || null,
      }),
    onSuccess: () => {
      setText('');
      setMessage('');
      queryClient.invalidateQueries({ queryKey: ['invitations', org.id] });
    },
  });
  const tooMany = emails.length > MAX_INVITES_PER_SEND;

  return (
    <Card title="Invite by email">
      <form
        className="max-w-3xl space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (emails.length && !bad.length && !tooMany) send.mutate();
        }}
      >
        <Field
          label="Email addresses"
          htmlFor="invite-emails"
          hint={`Separate with commas, spaces or new lines; up to ${MAX_INVITES_PER_SEND} at a time.`}
        >
          <Textarea
            id="invite-emails"
            rows={3}
            value={text}
            placeholder="asha@example.org, ravi@example.org"
            onChange={(e) => setText(e.target.value)}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Joins as" htmlFor="invite-role">
            <Select id="invite-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {INVITE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Group (optional)" htmlFor="invite-group">
            <Select id="invite-group" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <option value="">No group</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="Personal note (optional)" htmlFor="invite-message">
          <Textarea
            id="invite-message"
            rows={2}
            maxLength={500}
            value={message}
            placeholder="Shown in the email, e.g. why you're inviting them."
            onChange={(e) => setMessage(e.target.value)}
          />
        </Field>
        {bad.length > 0 && (
          <p className="text-sm text-destructive">
            Check {bad.length === 1 ? 'this address' : 'these addresses'}: {bad.join(', ')}
          </p>
        )}
        {tooMany && (
          <p className="text-sm text-destructive">
            That&rsquo;s {emails.length} addresses; send up to {MAX_INVITES_PER_SEND} at a time.
          </p>
        )}
        <Button
          type="submit"
          className="w-full sm:w-auto"
          disabled={!emails.length || bad.length > 0 || tooMany || send.isPending}
        >
          <Send className="size-4" aria-hidden />
          {send.isPending
            ? 'Sending…'
            : emails.length > 1
              ? `Send ${emails.length} invitations`
              : 'Send invitation'}
        </Button>
        {send.data && (
          <p className="text-sm text-muted-foreground" role="status">
            {send.data.invited.length > 0 &&
              `Sent ${send.data.invited.length} invitation${send.data.invited.length === 1 ? '' : 's'}. `}
            {send.data.alreadyMembers.length > 0 &&
              `Already members: ${send.data.alreadyMembers.join(', ')}.`}
          </p>
        )}
        <ErrorText error={send.error} />
      </form>
    </Card>
  );
}

/** Invitations not accepted yet: resend with a fresh link, or withdraw. */
function PendingInvitations() {
  const { org } = useOrgContext();
  const api = useApi();
  const queryClient = useQueryClient();
  const invitations = useQuery({
    queryKey: ['invitations', org.id],
    queryFn: () => api.invitations(org.id),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['invitations', org.id] });
  const resend = useMutation({
    mutationFn: (id: string) => api.resendInvitation(org.id, id),
    onSuccess: refresh,
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeInvitation(org.id, id),
    onSuccess: refresh,
  });
  const list = invitations.data?.invitations ?? [];
  if (!list.length) return null;
  return (
    <Card title={`Waiting to accept (${list.length})`}>
      <ul className="divide-y divide-border">
        {list.map((i) => (
          <li key={i.id} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
            <div className="min-w-0">
              <p className="break-all font-medium">{i.email}</p>
              <p className="text-muted-foreground">
                {ROLE_LABELS[i.role]}
                {i.groupName ? ` · ${i.groupName}` : ''} ·{' '}
                {i.expired ? (
                  <span className="text-destructive">Expired {formatDate(i.expiresAt)}</span>
                ) : (
                  `Sent ${formatDate(i.lastSentAt ?? i.createdAt)}`
                )}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                disabled={resend.isPending}
                onClick={() => resend.mutate(i.id)}
              >
                Resend
              </Button>
              <Button
                variant="ghost"
                disabled={revoke.isPending}
                onClick={() => {
                  if (window.confirm(`Withdraw the invitation to ${i.email}?`)) revoke.mutate(i.id);
                }}
              >
                Withdraw
              </Button>
            </div>
          </li>
        ))}
      </ul>
      <ErrorText error={resend.error ?? revoke.error} />
    </Card>
  );
}
